import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BannerDTO } from '../../../interfaces/banner-management/banner-dto.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BannerDisplayTypeEnum } from '../../../enums/banner-management/banner-display-type.enum';
import { BannerDisplayWeekEnum } from '../../../enums/banner-management/banner-display-week.enum';
import { ConfirmationService, MessageService, SelectItem } from 'primeng';
import { BannerManagementService } from '../../../services/banner-management.service';
import { FileStorageService } from '../../../services';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { Message } from '../../../classes/Message';
import { compare } from 'fast-json-patch';
import moment = require('moment');

interface ImageDimensions {
  width: number;
  height: number;
}

@Component({
  selector: 'app-banner-edit',
  templateUrl: './banner-edit.component.html',
  styleUrls: [
    './banner-edit.component.scss',
    './../banner-list/banner-management-list.component.scss',
  ],
})
export class BannerEditComponent implements OnInit {
  @Output() bannerEditFinished = new EventEmitter<BannerDTO>();

  _editBanner: BannerDTO;

  @Input('editBanner')
  public set editBanner(value: BannerDTO) {
    this._editBanner = value;

    if (this._editBanner) {
      if (!this.form) {
        this.initForm();
      }

      // patch form with existing banner details
      this._editBanner.showFrom = new Date(this._editBanner.showFrom);
      if (this._editBanner.showUntil) {
        this._editBanner.showUntil = new Date(this._editBanner.showUntil);
      }

      this.form.patchValue(this._editBanner);
      this.preEditForm = Object.assign({}, this.form);

      this.presignedImageUrl = this._editBanner.presignedImageURL;
      this.files = [
        {
          imageLocation: this._editBanner.imageLocation,
          fileName: '',
        },
      ];
    } else {
      // reset form to clear values
      this.initForm();
      this.files = [];
    }
  }
  public get editBanner(): BannerDTO {
    return this._editBanner;
  }

  public BannerDisplayType = BannerDisplayTypeEnum;

  bannerCategories: SelectItem[];
  displayWeekValues: SelectItem[];
  bannerPositionValues: SelectItem[];

  files: any = [];
  savingImage = false;
  loadingImage = false;
  presignedImageUrl: string = null;

  form: FormGroup;
  preEditForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private bannerService: BannerManagementService,
    private fileStorageService: FileStorageService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit() {
    this.createDisplayWeekValues();
    this.createBannerPositionValues();

    this.bannerService.getBannerCategories().then((res) => {
      this.bannerCategories = res.map((x) => {
        return { label: x.name, value: x.id };
      });
      this.bannerCategories.unshift({ label: 'Select Type', value: null });
    });
  }

  get f() {
    return this.form.controls;
  }

  initForm() {
    this.form = this.formBuilder.group({
      title: ['', Validators.required],
      bannerCategoryId: [null, Validators.required],
      description: ['', Validators.required],
      displayType: [null, Validators.required],
      displayWeek: [BannerDisplayWeekEnum.All, Validators.required],
      showFrom: [new Date()],
      showFromString: [''],
      showUntil: [null],
      positionInCarouselWeekdays: [1, Validators.required],
      positionInCarouselWeekends: [1, Validators.required],
      notes: [''],
      imageLocation: [null],
    });

    this.presignedImageUrl = null;
  }

  createDisplayWeekValues() {
    this.displayWeekValues = [];
    Object.keys(BannerDisplayWeekEnum)
      .filter((key) => isNaN(Number(BannerDisplayWeekEnum[key])))
      .forEach((x) => {
        this.displayWeekValues.push({
          label:
            BannerDisplayWeekEnum[x] === BannerDisplayWeekEnum.All
              ? 'Show all week'
              : BannerDisplayWeekEnum[x],
          value: x,
        });
      });
  }

  createBannerPositionValues() {
    this.bannerPositionValues = [];
    for (let i = 1; i < 8; i++) {
      this.bannerPositionValues.push({ label: `${this.getOrdinalSuffix(i)} Banner`, value: i });
    }
  }

  getOrdinalSuffix(number: number): string {
    let suffix = 'th';

    if (number % 10 == 1 && number % 100 != 11) {
      suffix = 'st';
    } else if (number % 10 == 2 && number % 100 != 12) {
      suffix = 'nd';
    } else if (number % 10 == 3 && number % 100 != 13) {
      suffix = 'rd';
    }

    return number + suffix;
  }

  async submitForm() {
    if (!this.form.valid || !this.presignedImageUrl) {
      return;
    }

    const banner = this.form.value as BannerDTO;

    if (this._editBanner?.id > 0) {
      this.preEditForm.value.showFrom = moment(this.preEditForm.value.showFrom).format(
        'YYYY-MM-DD',
      );
      if (this.preEditForm.value.showUntil != null) {
        this.preEditForm.value.showUntil = moment(this.preEditForm.value.showUntil).format(
          'YYYY-MM-DD',
        );
      }

      let newEditForm = Object.assign({}, this.form.value);
      newEditForm.showFrom = moment(this.form.value.showFrom).format('YYYY-MM-DD');
      if (this.form.value.showUntil != null) {
        newEditForm.showUntil = moment(this.form.value.showUntil).format('YYYY-MM-DD');
      }

      const patch = compare(this.preEditForm.value, newEditForm);

      this.bannerService
        .patchBanner(this._editBanner.id, patch)
        .then((res) => {
          this.bannerEditFinished.emit(res);
        })
        .catch((err) => {
          this.handleError(err);
        });
    } else {
      // save the banner
      await this.bannerService
        .saveBanner(banner)
        .then((res) => {
          this.bannerEditFinished.emit(res);
        })
        .catch((err) => {
          this.handleError(err);
        });
    }
  }

  private handleError(err) {
    let errStr = err.error as string;

    if (errStr?.toLowerCase().includes('maximum')) {
      // not ideal, we could do with a patten for specific exception handling
      this.messageService.add(Message.Error('Maximum active banners already in use'));
    } else {
      this.messageService.add(Message.Error('An error occurred saving the banner'));
    }
  }

  async dropped(files: NgxFileDropEntry[]) {
    if (files && files.length > 1) {
      this.messageService.add(Message.Error('Only one image allowed per banner'));
      return;
    }

    let droppedFile = files[0];

    // Is it a file?
    if (droppedFile.fileEntry.isFile) {
      // check the file size
      const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

      // need to verify max dimensions
      // const dimensions = await this.getImageDimensions(fileEntry);
      // if (dimensions.width > 800 || dimensions.height > 300) {
      //   this.messageService.add(Message.Error('The image is larger than allowed dimensions'));
      //   return;
      // }

      // save the file now so we can preview it with a pre-signed url
      await this.saveFile(droppedFile.fileEntry as FileSystemFileEntry);
    }
  }

  removeFile(event) {
    event.preventDefault();
    event.stopPropagation();

    console.log('Files: ', this.files);
    const file = this.files[0];

    this.confirmationService.confirm({
      message: `Are you sure you want to delete the image?`,
      header: 'Delete Image?',
      acceptLabel: 'Continue',
      rejectLabel: 'Cancel',
      accept: () => {
        // don't add to delete list if file not yet saved
        if (!file?.fileEntry) {
          // delete the file from storage now
          this.deleteFile(file.imageLocation);
        }
      },
    });
  }

  async cancelEdit() {
    // if we have a file, delete it from storage
    await this.deleteFile(this.files[0]).catch(() => {
      this.bannerEditFinished.emit(null);
    });

    this.bannerEditFinished.emit(null);
  }

  async deleteFile(imageLocation) {
    await this.fileStorageService.deleteFile(imageLocation).then((res) => {
      if (res) {
        this.files = [];
        this.form.get('imageLocation').setValue(null);
        this.presignedImageUrl = null;
      }
    });
  }

  async saveFile(fileEntry: FileSystemFileEntry) {
    await fileEntry.file(async (file: File) => {
      await this.fileStorageService.uploadBannerFile(file).then((res) => {
        if (res.successfully) {
          this.form.get('imageLocation').setValue(res.fileData.guid);
          this.files = [
            {
              imageLocation: res.fileData.guid,
              fileName: file.name,
            },
          ];

          this.loadingImage = true;

          this.fileStorageService.getPreSignedURLs([res.fileData.guid]).then((url) => {
            this.presignedImageUrl = url.length ? url[0] : null;
            this.loadingImage = false;
          });
        } else {
          this.messageService.add(Message.Error('File could not be uploaded'));
        }
      });
    });
  }

  statusClass(status) {
    return `status-${status}`;
  }
}
